import React, { useRef, useState } from 'react';

const DEFAULT_MAX = Number.POSITIVE_INFINITY;

/**
 * @param {object} props
 * @param {string} [props.label]
 * @param {string} props.previewUrl
 * @param {(dataUrl: string) => void} props.onChange
 * @param {(message: string) => void} props.onError
 * @param {boolean} [props.disabled]
 * @param {number} [props.maxBytes]
 */
export default function ShopPhotoPicker({
  label = 'Shop pic',
  previewUrl,
  onChange,
  onError,
  disabled = false,
  maxBytes = DEFAULT_MAX,
}) {
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const processFile = (e) => {
    const input = e.target;
    const f = input?.files?.[0];
    input.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      onError('Please choose an image file.');
      return;
    }
    if (f.size > maxBytes) {
      onError('Image is too large.');
      return;
    }
    onError('');
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') onChange(reader.result);
    };
    reader.readAsDataURL(f);
  };

  const openFromGallery = () => {
    setSourceModalOpen(false);
    galleryInputRef.current?.click();
  };

  const openCamera = () => {
    setSourceModalOpen(false);
    cameraInputRef.current?.click();
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={processFile}
        disabled={disabled}
        aria-hidden
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={processFile}
        disabled={disabled}
        aria-hidden
      />

      {!previewUrl && (
        <button
          type="button"
          onClick={() => setSourceModalOpen(true)}
          disabled={disabled}
          className="w-full border border-dashed border-gray-300 rounded-lg py-3 px-4 text-gray-700 bg-gray-50 hover:bg-gray-100 disabled:opacity-50 min-h-[44px]"
        >
          Add photo
        </button>
      )}

      {previewUrl && (
        <div className="mt-0 space-y-2">
          <button
            type="button"
            onClick={() => setZoomOpen(true)}
            disabled={disabled}
            className="block text-left disabled:opacity-50"
            title="Tap to zoom"
          >
            <img src={previewUrl} alt="Shop" className="h-32 w-auto max-w-full rounded-lg border object-contain" />
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSourceModalOpen(true)}
              disabled={disabled}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              Change photo
            </button>
            <button
              type="button"
              onClick={() => {
                onChange('');
                onError('');
                setZoomOpen(false);
              }}
              disabled={disabled}
              className="text-sm text-gray-600 hover:text-gray-900 underline disabled:opacity-50"
            >
              Remove image
            </button>
          </div>
        </div>
      )}

      {zoomOpen && previewUrl && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6 bg-black/80"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setZoomOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setZoomOpen(false)}
              className="absolute -top-3 -right-3 sm:top-0 sm:right-0 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow"
              aria-label="Close image preview"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={previewUrl}
              alt="Shop"
              className="w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {sourceModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shop-photo-source-title"
          onClick={() => setSourceModalOpen(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-sm p-4 sm:p-5 sm:mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="shop-photo-source-title" className="text-center text-base font-semibold text-gray-800 mb-3">
              Add shop photo
            </h3>
            <p className="text-center text-sm text-gray-600 mb-4">How do you want to add the image?</p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={openFromGallery}
                className="w-full py-3 px-4 rounded-lg border border-gray-200 bg-white text-gray-800 font-medium hover:bg-gray-50 min-h-[48px] touch-manipulation"
              >
                Choose from gallery
              </button>
              <button
                type="button"
                onClick={openCamera}
                className="w-full py-3 px-4 rounded-lg border border-gray-200 bg-white text-gray-800 font-medium hover:bg-gray-50 min-h-[48px] touch-manipulation"
              >
                Take a photo
              </button>
              <button
                type="button"
                onClick={() => setSourceModalOpen(false)}
                className="w-full py-2.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
